import { Link } from "react-router-dom";
import Hangtag from "./Hangtag";
import { useAuth } from "../lib/auth";
import { resolveImage } from "../lib/format";
import type { Product } from "../lib/types";

/**
 * One product in the grid: the cut-out photograph with its hangtag beside it,
 * the way the garment is presented in the store.
 *
 * A product is listed once here regardless of how many pages render it — the
 * page structure is a fact about the storefront, not about the product, so it
 * belongs on the detail screen and the site map rather than fragmenting this
 * listing.
 *
 * The inner wrapper exists so a container query can re-flow the pair: a card in
 * a four-up grid is narrower than the tag plus a usable photograph, so at that
 * size the tag drops beneath the image instead of squeezing beside it. A
 * container query cannot style its own container, hence the extra element.
 */

export interface ProductCardProps {
  product: Product;
  watched?: boolean;
  watchedSizes?: string[];
  onToggleWatch?: () => void;
  onToggleSize?: (size: string) => void;
  priority?: boolean;
}

export default function ProductCard({
  product,
  watched,
  watchedSizes,
  onToggleWatch,
  onToggleSize,
  priority = false,
}: ProductCardProps) {
  const { isAdmin } = useAuth();
  const src = resolveImage(product.featuredImage ?? product.images[0]);
  // Clicking a piece should take you to the thing itself. The watcher's own
  // history lives behind a separate, quieter link for operators.
  const storeUrl = product.url;

  return (
    <article className="ap-card">
      <div className="ap-card__inner">
        <div className="ap-card__media">
          {src ? (
            storeUrl ? (
              <a
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${product.title} on the store`}
              >
                <img
                  src={src}
                  alt={product.title}
                  loading={priority ? "eager" : "lazy"}
                  decoding="async"
                />
              </a>
            ) : (
              <img
                src={src}
                alt={product.title}
                loading={priority ? "eager" : "lazy"}
                decoding="async"
              />
            )
          ) : (
            <div className="ap-card__noimage" aria-hidden="true" />
          )}
        </div>

        <div className="ap-card__tag">
          <Hangtag
            product={product}
            href={storeUrl}
            watched={watched}
            watchedSizes={watchedSizes}
            onToggleWatch={onToggleWatch}
            onToggleSize={onToggleSize}
          />
          {isAdmin && (
            <Link className="ap-card__history" to={`/p/${product.productId}`}>
              history
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
